import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { refreshTrixEditors } from "../utils/refresh_trix_editors"

const DEEP_LINK_PARAMS = ["comment_thread_id", "comment_id"]

export default class extends Controller {
  static targets = [
    "threadList",
    "mainPanel",
    "searchInput",
    "threadItem",
    "messagesContainer",
    "newThreadTemplate",
    "threadPanelTemplate",
    "emptyThreads"
  ]
  static values = { selectedThreadId: Number }

  connect() {
    this.boundHandleCommentCreated = this.handleCommentCreated.bind(this)
    document.addEventListener("comment:created", this.boundHandleCommentCreated)
    this.bindModalEvents()
    this.syncMobileLayout()
    // Après coup : le contrôleur `modal` déplace la modale dans #modals à son branchement, ce
    // qui débranche puis rebranche celui-ci.
    this.deepLinkTimer = setTimeout(() => this.openDeepLinkedThread(), 0)
  }

  // Lien d'une notification (CommentThreadsController#show) : `comment_thread_id` ouvre la
  // fenêtre des notes sur la discussion, `comment_id` y amène le message concerné.
  openDeepLinkedThread() {
    const params = new URLSearchParams(window.location.search)
    const threadId = params.get("comment_thread_id")
    if (!threadId || !this.element.isConnected) return
    if (!this.threadPanelTemplateTargets.some((el) => el.dataset.threadId === threadId)) return

    this.forgetDeepLink()
    this.pendingCommentId = params.get("comment_id")
    this.deepLinked = true
    this.highlightThread(threadId)

    const modalRoot = this.modalRootElement()
    if (modalRoot) {
      // onModalShown recharge le panneau de la discussion sélectionnée.
      Modal.getOrCreateInstance(modalRoot).show()
    } else {
      this.loadThreadPanel(threadId)
      if (this.isMobileLayout()) this.showThreadView()
    }
  }

  // Le lien est consommé : un retour arrière (cache Turbo) ne rouvre pas la fenêtre qu'on a
  // fermée, alors qu'un nouveau clic sur la notification la rouvre.
  forgetDeepLink() {
    const url = new URL(window.location.href)
    DEEP_LINK_PARAMS.forEach((name) => url.searchParams.delete(name))
    window.history.replaceState(window.history.state, "", url)
  }

  disconnect() {
    clearTimeout(this.deepLinkTimer)
    document.removeEventListener("comment:created", this.boundHandleCommentCreated)
    this.unbindModalEvents()
    this.mobileMediaQuery?.removeEventListener("change", this.boundSyncMobileLayout)
  }

  syncMobileLayout() {
    if (!this.boundSyncMobileLayout) {
      this.boundSyncMobileLayout = this.syncMobileLayout.bind(this)
      this.mobileMediaQuery = window.matchMedia("(max-width: 991.98px)")
      this.mobileMediaQuery.addEventListener("change", this.boundSyncMobileLayout)
    }

    if (!this.isMobileLayout()) {
      this.element.classList.remove("comments-workspace--list-view", "comments-workspace--thread-view")
      this.syncModalViewClass()
      return
    }

    if (this.element.classList.contains("comments-workspace--thread-view")) return

    if (this.threadItemTargets.length === 0) {
      this.showThreadView()
    } else {
      this.showListView()
    }
  }

  isMobileLayout() {
    return document.body.classList.contains("turbo-native") ||
      window.matchMedia("(max-width: 991.98px)").matches
  }

  showListView() {
    this.element.classList.add("comments-workspace--list-view")
    this.element.classList.remove("comments-workspace--thread-view")
    this.syncModalViewClass()
  }

  showThreadView() {
    this.element.classList.add("comments-workspace--thread-view")
    this.element.classList.remove("comments-workspace--list-view")
    this.syncModalViewClass()
  }

  syncModalViewClass() {
    const modalRoot = this.modalRootElement()
    if (!modalRoot) return

    modalRoot.classList.remove("comments-modal--list-view", "comments-modal--thread-view")

    if (!this.isMobileLayout()) return

    if (this.element.classList.contains("comments-workspace--thread-view")) {
      modalRoot.classList.add("comments-modal--thread-view")
    } else if (this.element.classList.contains("comments-workspace--list-view")) {
      modalRoot.classList.add("comments-modal--list-view")
    }
  }

  modalRootElement() {
    return this.modalElement || this.element.closest(".comments-modal-root")
  }

  closeModal(event) {
    event?.preventDefault()

    const modalRoot = this.modalRootElement()
    if (!modalRoot) return

    Modal.getOrCreateInstance(modalRoot).hide()
  }

  handleBack(event) {
    event?.preventDefault()
    if (!this.isMobileLayout()) return

    if (this.threadItemTargets.length === 0) {
      this.closeModal()
      return
    }

    this.showThreadList()
  }

  showThreadList(event) {
    event?.preventDefault()
    if (!this.isMobileLayout()) return

    this.showListView()
    this.threadListTarget?.scrollTo({ top: 0, behavior: "smooth" })
  }

  bindModalEvents() {
    this.unbindModalEvents()

    const modalRoot = this.modalRootElement()
    if (!modalRoot) return

    this.modalElement = modalRoot
    this.boundModalShown = () => this.onModalShown()
    modalRoot.addEventListener("shown.bs.modal", this.boundModalShown)

    if (modalRoot.classList.contains("show")) {
      this.onModalShown()
    }
  }

  unbindModalEvents() {
    if (this.modalElement && this.boundModalShown) {
      this.modalElement.removeEventListener("shown.bs.modal", this.boundModalShown)
    }
    this.modalElement = null
  }

  // Trix mounts without a toolbar while the Bootstrap modal is display:none.
  // Remounting in place is unreliable on first open; reinjecting the panel HTML
  // (same path as thread switching) always works once the modal is visible.
  onModalShown() {
    requestAnimationFrame(() => {
      if (this.isMobileLayout()) {
        if (this.threadItemTargets.length === 0 || this.deepLinked) {
          this.showThreadView()
        } else {
          this.showListView()
        }
      }
      this.deepLinked = false

      this.reloadCurrentPanel({ refreshTrix: true })
    })
  }

  reloadCurrentPanel({ refreshTrix = true } = {}) {
    const threadId = this.selectedThreadIdValue

    if (threadId) {
      this.loadThreadPanel(String(threadId), { refreshTrix })
      return
    }

    if (!this.hasNewThreadTemplateTarget || !this.hasMainPanelTarget) return

    this.mainPanelTarget.innerHTML = this.newThreadTemplateTarget.innerHTML
    if (refreshTrix) refreshTrixEditors(this.mainPanelTarget, { force: true })
  }

  selectThread(event) {
    const threadId = event.currentTarget.dataset.threadId
    if (!threadId) return

    this.highlightThread(threadId)
    this.loadThreadPanel(threadId)
    if (this.isMobileLayout()) this.showThreadView()
  }

  showNewThread() {
    this.threadItemTargets.forEach((item) => item.classList.remove("comments-thread-list-item--selected"))
    this.selectedThreadIdValue = 0

    if (!this.hasNewThreadTemplateTarget) return

    this.mainPanelTarget.innerHTML = this.newThreadTemplateTarget.innerHTML
    refreshTrixEditors(this.mainPanelTarget, { force: true })

    if (this.isMobileLayout()) this.showThreadView()

    const titleField = this.mainPanelTarget.querySelector('[data-comment-form-target="titleField"]')
    if (titleField) {
      // Après remount Trix, focus au frame suivant pour éviter un flash focus perdu
      requestAnimationFrame(() => titleField.focus({ preventScroll: true }))
    }
  }

  filterThreads() {
    const query = this.searchInputTarget.value.toLowerCase().trim()

    this.threadItemTargets.forEach((item) => {
      if (query.length === 0) {
        item.classList.remove("d-none")
        return
      }

      const haystack = [
        item.dataset.threadTitle,
        item.dataset.threadAuthor,
        item.dataset.threadBody,
      ]
        .filter(Boolean)
        .join(" ")

      item.classList.toggle("d-none", !haystack.includes(query))
    })
  }

  highlightThread(threadId) {
    const id = String(threadId)
    this.threadItemTargets.forEach((item) => {
      item.classList.toggle("comments-thread-list-item--selected", item.dataset.threadId === id)
    })
    this.selectedThreadIdValue = Number(threadId)
  }

  loadThreadPanel(threadId, { refreshTrix = true } = {}) {
    const template = this.threadPanelTemplateTargets.find(
      (el) => el.dataset.threadId === String(threadId)
    )
    if (!template || !this.hasMainPanelTarget) return

    this.mainPanelTarget.innerHTML = template.innerHTML
    if (refreshTrix) refreshTrixEditors(this.mainPanelTarget, { force: true })

    const commentId = this.pendingCommentId
    this.pendingCommentId = null
    if (!commentId || !this.revealComment(commentId)) this.scrollMessagesToBottom()
  }

  // Amène le message au centre et le signale le temps d'une animation.
  revealComment(commentId) {
    const message = Array.from(this.mainPanelTarget.querySelectorAll(".comments-message[data-comment-id]"))
      .find((el) => el.dataset.commentId === String(commentId))
    if (!message) return false

    message.scrollIntoView({ block: "center" })
    message.classList.remove("comments-message--highlight")
    void message.offsetWidth
    message.classList.add("comments-message--highlight")
    message.addEventListener("animationend", () => message.classList.remove("comments-message--highlight"), { once: true })
    return true
  }

  handleCommentCreated() {
    this.scrollMessagesToBottom()
  }

  scrollMessagesToBottom() {
    if (!this.hasMessagesContainerTarget) return

    const container = this.messagesContainerTarget
    container.scrollTop = container.scrollHeight
  }
}
